/**
 * Scenario calculation utility.
 *
 * Provides a pure function to calculate retirement metrics from raw scenario settings.
 * Reuses the logic from useRetirementStore.calculateRetirement while handling
 * age-based defaults provided from the application context.
 *
 * Constructs temporary Settings class instances from scenario data so that
 * field lists and default-fallback logic live in one place (the Settings classes),
 * eliminating duplication with useProjections.
 */

import BigNumber from "bignumber.js";
import { calculateRetirement } from "../store/retirementStore";
import type { RetirementResult } from "../store/retirementStore";
import type { ScenarioSettings } from "../types/scenario";
import type { AgeStatistics } from "../types/statistics";
import {
  InvestableAssetsSettings,
  RealEstateAssetsSettings,
  LiabilitiesSettings,
  IncomeSettings,
  ExpensesSettings,
  AssumptionsSettings,
} from "../contexts/SettingsContext";
import type {
  InvestableAssetsSettingsData,
  RealEstateAssetsSettingsData,
  LiabilitiesSettingsData,
  IncomeSettingsData,
  ExpensesSettingsData,
  AssumptionsSettingsData,
} from "../types/settings";

/**
 * Pre-fill missing or null/undefined fields in scenario data with
 * age-appropriate statistics defaults. SettingsBase.getNumericFieldValue
 * returns 0 for missing fields (correct for main context), but scenarios
 * need statistics-based defaults for unset fields so that comparisons
 * use realistic age-average values rather than zero.
 */
function fillFromStats<T>(data: Partial<T>, stats: AgeStatistics): T {
  const filled: Record<string, unknown> = { ...data };
  for (const [fieldName, stat] of Object.entries(stats)) {
    if (
      !(fieldName in filled) ||
      filled[fieldName] === null ||
      filled[fieldName] === undefined
    ) {
      filled[fieldName] = stat.average;
    }
  }
  return filled as T;
}

/**
 * Calculate metrics for a scenario based on its captured settings.
 *
 * This function replicates the logic in useProjections.ts but for static scenario settings.
 * It uses the provided per-age statistics map to fill in fallback defaults for any fields
 * that were not explicitly set in the scenario (currently null in storage).
 *
 * @param settings - Captured scenario settings (from Scenario object)
 * @param age - User's current age, used for RetirementData.currentAge
 * @param statistics - Per-age statistics map for defaults
 * @returns Projection results for the scenario
 */
export function calculateScenarioMetrics(
  settings: ScenarioSettings,
  age: number,
  statistics: AgeStatistics
): RetirementResult {
  // Construct Settings class instances from scenario data.
  // Pre-fill missing fields with statistics so unset scenario fields use
  // realistic age-average values rather than zero.
  const investableAssets = new InvestableAssetsSettings(
    fillFromStats<InvestableAssetsSettingsData>(
      settings.investableAssets,
      statistics
    ),
    age,
    statistics
  );
  const realEstateAssets = new RealEstateAssetsSettings(
    fillFromStats<RealEstateAssetsSettingsData>(
      settings.realEstateAssets,
      statistics
    ),
    age,
    statistics
  );
  const liabilities = new LiabilitiesSettings(
    fillFromStats<LiabilitiesSettingsData>(settings.liabilities, statistics),
    age,
    statistics
  );
  const income = new IncomeSettings(
    fillFromStats<IncomeSettingsData>(settings.income, statistics),
    age,
    statistics
  );
  const expenses = new ExpensesSettings(
    fillFromStats<ExpensesSettingsData>(settings.expenses, statistics),
    age,
    statistics
  );
  const assumptions = new AssumptionsSettings(
    fillFromStats<AssumptionsSettingsData>(settings.assumptions, statistics),
    age,
    statistics
  );

  const monthlyIncome = income.monthlyIncome;
  const monthlyExpenses = expenses.monthlyExpenses;

  const monthlyContribution = BigNumber.max(
    new BigNumber(0),
    new BigNumber(monthlyIncome).minus(monthlyExpenses)
  ).toNumber();

  const totalAssets = new BigNumber(investableAssets.totalInvestableAssets)
    .plus(realEstateAssets.totalRealEstateAssets)
    .toNumber();

  const currentSavings = new BigNumber(totalAssets)
    .minus(liabilities.totalLiabilities)
    .toNumber();

  const data = {
    currentAge: age,
    retirementAge: assumptions.retirementAge,
    lifeExpectancy: assumptions.lifeExpectancy,
    currentSavings,
    monthlyContribution,
    annualReturn: assumptions.expectedReturnRate,
    inflationRate: assumptions.inflationRate,
    monthlyExpenses,
    expensesAtRetirement: assumptions.expensesAtRetirement,
    currency: "CAD" as const,
    debts: {
      mortgage: {
        balance: liabilities.mortgage,
        interestRate: liabilities.mortgageInterestRate,
        monthlyPayment: expenses.mortgagePayment,
      },
      creditCard: {
        balance: liabilities.creditCardBalance,
        interestRate: liabilities.creditCardInterestRate,
        monthlyPayment: expenses.creditCardPayment,
      },
      carLoan: {
        balance: liabilities.carLoan,
        interestRate: liabilities.carLoanInterestRate,
        monthlyPayment: expenses.carLoanPayment,
      },
      lineOfCredit: {
        balance: liabilities.lineOfCredit,
        interestRate: liabilities.lineOfCreditInterestRate,
        monthlyPayment: expenses.lineOfCreditPayment,
      },
      studentLoan: {
        balance: liabilities.studentLoans,
        interestRate: liabilities.studentLoanInterestRate,
        monthlyPayment: expenses.studentLoanPayment,
      },
      otherLiabilities: {
        balance: liabilities.otherLiabilities,
        interestRate: liabilities.otherLiabilitiesInterestRate,
        monthlyPayment: expenses.otherLiabilitiesPayment,
      },
    },
  };

  return calculateRetirement(data);
}
